import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/** One client due (or overdue) for outreach — mirrors the backend DueEventRow. */
export interface DueClientEventRow {
  eventId: string;
  clientId: string;
  displayName: string;
  primaryContactName: string | null;
  primaryBrokerDisplay: string | null;
  officeId: string | null;
  officeName: string | null;
  email: string | null;
  phone: string | null;
  preferredContactMethod: 'phone' | 'email' | 'sms' | 'any' | null;
  ambiguousIdentity: boolean;
  eventType: string;
  /** Human reason, e.g. "fixed-rate roll-off". */
  label: string;
  dueAtMs: number;
  /** <0 overdue by N days, 0 due today, >0 due in N days (Brisbane civil days). */
  daysFromToday: number;
  overdue: boolean;
}

interface ListDueClientEventsResponse {
  overdue: DueClientEventRow[];
  dueThisWeek: DueClientEventRow[];
  counts: { overdue: number; dueThisWeek: number };
}

interface UseDueClientEventsParams {
  companyId?: string;
  /** Per-column row cap (default backend = 50). */
  limit?: number;
  /** Attribute filters (mirror the table). `overdueOnly` is not applicable — the
   *  panel's left column IS the overdue feed. */
  officeId?: string | null;
  brokerDisplay?: string | null;
  loanBookEntityCode?: string | null;
  status?: 'active' | 'inactive' | null;
}

/**
 * RBAC-scoped "Who To Call" feed via the listDueClientEvents callable
 * (broker → own clients · assistant/analyst → linked brokers · office admin →
 * offices · super_admin → all). Scope is enforced server-side.
 */
export function useDueClientEvents({
  companyId,
  limit,
  officeId = null,
  brokerDisplay = null,
  loanBookEntityCode = null,
  status = null,
}: UseDueClientEventsParams) {
  const [overdue, setOverdue] = useState<DueClientEventRow[]>([]);
  const [dueThisWeek, setDueThisWeek] = useState<DueClientEventRow[]>([]);
  const [counts, setCounts] = useState<{ overdue: number; dueThisWeek: number }>({ overdue: 0, dueThisWeek: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<Record<string, unknown>, ListDueClientEventsResponse>(functions, 'listDueClientEvents');
      const res = await fn({
        companyId,
        limit,
        officeId: officeId || undefined,
        brokerDisplay: brokerDisplay || undefined,
        loanBookEntityCode: loanBookEntityCode || undefined,
        status: status || undefined,
      });
      setOverdue(res.data.overdue ?? []);
      setDueThisWeek(res.data.dueThisWeek ?? []);
      setCounts(res.data.counts ?? { overdue: 0, dueThisWeek: 0 });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load call list';
      setError(msg);
      console.error('[useDueClientEvents] failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId, limit, officeId, brokerDisplay, loanBookEntityCode, status]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { overdue, dueThisWeek, counts, loading, error, refetch };
}
